import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import advanced from 'dayjs/plugin/advancedFormat';

import isBetween from 'dayjs/plugin/isBetween';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import Logger, { LoggingModule } from 'services/Logger';

import spacetime from 'spacetime';
import timezoneSoft from 'timezone-soft';

import { isIE } from 'lib/browser';

dayjs.extend(isBetween);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advanced);
dayjs.extend(customParseFormat);

export type DateStr = string | Date | Dayjs;

/*
  get MMM Do, YYYY from isoDate string (ex. Aug 2nd, 2023)
*/
export const formatDateOrdinal = (isoDate: DateStr): string => {
  try {
    return isoDate ? dayjs(isoDate).format('MMM Do, YYYY') : '';
  } catch (error) {
    Logger.warn(
      `${LoggingModule.dateUtils}FAIL DateUtils:formatShortDate() isoDate=${isoDate}`,
      error
    );
    return '';
  }
};

/*
  get YYYY from isoDate string
*/
export const formatYear = (isoDate: DateStr): string => {
  try {
    return isoDate ? dayjs(isoDate).format('YYYY') : '';
  } catch (error) {
    Logger.warn(`${LoggingModule.dateUtils}FAIL DateUtils:formatYear() isoDate=${isoDate}`, error);
    return '';
  }
};

/*
  get MM-DD/-YYY from isoDate string
*/
export const formatMMDDYYYYDate = (isoDate: DateStr): string => {
  try {
    return isoDate ? dayjs(isoDate).format('MM-DD-YYYY') : '';
  } catch (error) {
    Logger.warn(
      `${LoggingModule.dateUtils}FAIL DateUtils:formatLongDate() isoDate=${isoDate}`,
      error
    );
    return '';
  }
};

/*
  get MM/DD/YYYY from isoDate string
*/
export const formatDate = (isoDate: DateStr): string => {
  try {
    if (!isoDate) {
      return '';
    }

    // Check if the date is already in MM/DD/YYYY format
    const mmddyyyyRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/;
    if (typeof isoDate === 'string' && mmddyyyyRegex.test(isoDate)) {
      return isoDate; // Return as-is if already formatted correctly
    }

    // IE does not support 2019-10-01T12:42:36.000+0000 format
    const validDate = isIE() && typeof isoDate === 'string' ? isoDate.slice(0, 10) : isoDate;
    return dayjs(validDate).format('MM/DD/YYYY');
  } catch (error) {
    Logger.warn(`${LoggingModule.dateUtils}FAIL DateUtils:formatDate() isoDate=${isoDate}`, error);
    return '';
  }
};

export const formatShortDate = (isoDate: DateStr): string => {
  try {
    return isoDate
      ? dayjs(isoDate).month() === 4
        ? dayjs(isoDate).format('MMM DD, YYYY')
        : dayjs(isoDate).format('MMM. DD, YYYY')
      : '';
  } catch (error) {
    Logger.warn(
      `${LoggingModule.dateUtils}FAIL DateUtils:formatShortDate() isoDate=${isoDate}`,
      error
    );
    return '';
  }
};

export const formatLongDate = (isoDate: DateStr): string => {
  try {
    return isoDate ? dayjs(isoDate).format('MMMM DD, YYYY') : '';
  } catch (error) {
    Logger.warn(
      `${LoggingModule.dateUtils}FAIL DateUtils:formatLongDate() isoDate=${isoDate}`,
      error
    );
    return '';
  }
};

/*
  get h:mm A from isoDate string
*/
export const formatTime = (isoDate: DateStr): string => {
  try {
    return isoDate ? dayjs(isoDate).format('h:mm A') : '';
  } catch (error) {
    Logger.warn(`${LoggingModule.dateUtils}FAIL DateUtils:formatTime() isoDate=${isoDate} `, error);
    return '';
  }
};

/*
  get h:mm A z from isoDate string
*/
export const formatTimeWithTimeZone = (isoDate: DateStr): string => {
  try {
    const isDateOnly = checkDateType(isoDate) === 'date-only';
    if (isDateOnly) return '';

    return isoDate
      ? dayjs(isoDate)
          .tz('America/Denver')
          .format('h:mm A z')
      : '';
  } catch (error) {
    Logger.warn(
      `${LoggingModule.dateUtils}FAIL DateUtils:formatTimeWithTimeZone() isoDate=${isoDate} `,
      error
    );
    return '';
  }
};

/*
  get h:mm:ss:SSS from isoDate string
*/
export const formatLongTime = (isoDate: DateStr): string => {
  try {
    return isoDate ? dayjs(isoDate).format('h:mm:ss:SSS') : '';
  } catch (error) {
    Logger.warn(`${LoggingModule.dateUtils}FAIL DateUtils:formatTime() isoDate=${isoDate} `, error);
    return '';
  }
};

/*
  get MM/DD/YYYY h:mm A from isoDate string
*/
export const formatDateWithTime = (isoDate: DateStr): string => {
  try {
    return isoDate ? dayjs(isoDate).format('MM/DD/YYYY h:mm A') : '';
  } catch (error) {
    Logger.warn(
      `${LoggingModule.dateUtils}FAIL DateUtils:formatDateWithTime() isoDate=${isoDate} `,
      error
    );
    return '';
  }
};

/*
  true: if date parameter is after today
*/
export const isAfterNow = (isoDate: DateStr): boolean => {
  try {
    return isoDate ? dayjs(isoDate).isAfter(dayjs()) : false;
  } catch (error) {
    Logger.warn(`${LoggingModule.dateUtils}FAIL DateUtils:isAfterNow() isoDate=${isoDate}`, error);
    return false;
  }
};

/*
  true: if date parameter plus number of days is after Now
*/
export const isAfterNowByDays = (isoDate: DateStr, days = 0): boolean => {
  try {
    return isoDate
      ? dayjs(isoDate)
          .add(days, 'day')
          .isAfter(dayjs())
      : false;
  } catch (error) {
    Logger.warn(
      `${LoggingModule.dateUtils}FAIL DateUtils:isAfterNowByDays() isoDate=${isoDate}`,
      error
    );
    return false;
  }
};

/*
  true: if date parameter plus number of minutes is after Now
*/
export const isAfterNowByMinutes = (isoDate: DateStr, minutes = 0): boolean => {
  try {
    return isoDate
      ? dayjs(isoDate)
          .add(minutes, 'minute')
          .isAfter(dayjs())
      : false;
  } catch (error) {
    Logger.warn(
      `${LoggingModule.dateUtils}FAIL DateUtils:isAfterNowByMinutes() isoDate=${isoDate}`,
      error
    );
    return false;
  }
};

/*
  true: if number of days have passed since date
*/
export const numberOfDaysHavePassedSinceDate = (days = 0, isoDate: DateStr): boolean => {
  try {
    return isoDate
      ? !dayjs(isoDate)
          .add(days, 'day')
          .isAfter(dayjs())
      : false;
  } catch (error) {
    Logger.warn(
      `${LoggingModule.dateUtils}FAIL DateUtils:isAfterNowByDays() isoDate=${isoDate}`,
      error
    );
    return false;
  }
};

/*
  returns true if targetDate is between two dates provided
  or true if after start date provided and no end date provided
  or true if before end date provided and no start date provided

  equal matches are always included
*/

export const isBetweenUnbounded = (
  isoTargetDate: DateStr,
  isoStartDate?: DateStr,
  isoEndDate?: DateStr
): boolean => {
  if (!isoTargetDate || (!isoStartDate && !isoEndDate)) return false;

  try {
    const targetDate = dayjs(isoTargetDate);
    const startDate = isoStartDate ? dayjs(isoStartDate) : false;
    const endDate = isoEndDate ? dayjs(isoEndDate) : false;

    if (startDate && endDate) return targetDate.isBetween(startDate, endDate, null, '[]');
    if (startDate) return targetDate.isSameOrAfter(startDate);
    if (endDate) return targetDate.isSameOrBefore(endDate);
  } catch (error) {
    Logger.warn(`${LoggingModule.dateUtils}FAIL DateUtils:isBetweenUnbounded()`, error);
  }

  return false;
};

export const formatPastDisplayDate = (date: DateStr) => {
  try {
    const currentDate = dayjs().startOf('day');
    const prevDate = dayjs(date).startOf('day');
    const dayDifference = currentDate.diff(prevDate, 'day');

    if (dayDifference <= 7) {
      if (dayDifference === 0) return 'Today';
      if (dayDifference === 1) return `Yesterday`;
      return `${dayDifference} days ago`;
    }

    return formatDate(prevDate);
  } catch (error) {
    Logger.warn(`${LoggingModule.dateUtils}FAIL DateUtils:formatPastDisplayDate()`, error);
    return '';
  }
};

export const formatFutureDisplayDate = (date: DateStr) => {
  const current = dayjs().startOf('day');
  const next = dayjs(date).startOf('day');
  const dayDifference = next.diff(current, 'day');

  if (dayDifference < 1) {
    return 'Today';
  }

  if (dayDifference < 2) {
    return 'Tomorrow';
  }

  if (dayDifference <= 7) {
    return `In ${dayDifference} days`;
  }

  if (dayDifference >= 365) {
    return next.format('MMM DD, YYYY');
  }

  return next.format('MMM DD');
};

/**
 * Generic displayDate fn returning either a past or future display date
 */
export const getDisplayDate = (date: string) => {
  const current = dayjs().startOf('day');
  const next = dayjs(date).startOf('day');
  const dayDifference = next.diff(current, 'day');

  if (dayDifference <= 0) {
    return formatPastDisplayDate(date);
  }

  return formatFutureDisplayDate(date);
};

/*
  get h:mm A in Mountain Time from isoDate string
*/
export const formatTimeToMountainTimeZone = (isoDate: DateStr): string => {
  try {
    return isoDate
      ? dayjs(isoDate)
          .tz('America/Denver')
          .format('h:mm A ')
      : '';
  } catch (error) {
    Logger.warn(
      `${LoggingModule.dateUtils}FAIL DateUtils:formatTimeWithTimeZone() isoDate=${isoDate} `,
      error
    );
    return '';
  }
};

/*
  get MMM DD string from isoDate
*/
export const formatMonthDate = (isoDate: DateStr): string => {
  try {
    return isoDate ? dayjs(isoDate).format('MMM DD') : '';
  } catch (error) {
    Logger.warn(
      `${LoggingModule.dateUtils}FAIL DateUtils:formatMonthDate() isoDate=${isoDate} `,
      error
    );
    return '';
  }
};

// get YYYY-MM-DD from MM/DD/YYYY
export const formatDateToIso8601 = (nonIsoDate: DateStr): string => {
  try {
    return nonIsoDate ? dayjs(nonIsoDate).format('YYYY-MM-DD') : '';
  } catch (error) {
    Logger.warn(`${LoggingModule.dateUtils}FAIL DateUtils:formatDateToIso8601()`, error);
    return String(nonIsoDate);
  }
};

export function calculateDaysTillFirstAvailability(
  currentDate: dayjs.Dayjs,
  earliestDate: string
): string {
  const firstAvailableDate = dayjs(earliestDate, 'YYYY-MM-DD');
  return Math.ceil(firstAvailableDate.diff(currentDate, 'day', true)).toString();
}

export function checkDateType(input: string | Date | dayjs.Dayjs) {
  const checkDate = dayjs(input, 'MM/DD/YYYY');

  // If invalid date, return an error message
  if (!checkDate.isValid()) {
    return 'invalid';
  }

  const hasTime = checkDate.hour() !== 0 || checkDate.minute() !== 0 || checkDate.second() !== 0;
  const hasDate = checkDate.year() !== 1970 || checkDate.month() !== 0 || checkDate.date() !== 1; // Avoid default time-only case

  if (hasDate && hasTime) return 'date-time';
  if (hasDate) return 'date-only';
  if (hasTime) return 'time-only';

  return 'unknown'; // Just in case of unexpected input
}

export const getLocalTimeWithZone = (isoDate: string) => {
  const spacetimeUTC = spacetime(isoDate, 'UTC');
  const currentTimezone = spacetime().timezone().name;
  const timezoneFullName = timezoneSoft(currentTimezone)[0].name;
  const localTime = spacetimeUTC.goto(currentTimezone);
  const localTimeFormatted = localTime.format('{time}');
  const localTimeFormattedWithZone = `${localTimeFormatted} ${timezoneFullName}`;
  return { localTimeFormattedWithZone, localTimeFormatted };
};
